import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import PanelCard, { EmptyState } from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Modal from '../../components/ui/Modal';
import { EditIcon, TrashIcon } from '../../components/ui/Icons';
import CopyButton from '../../components/ui/CopyButton';

const EMPTY_PRODUCT = { name: '', description: '', image_url: '', is_active: 1 };

/**
 * Titok megjelenítése: alapból maszkolt előnézet, a teljes érték csak akkor
 * kerül le a szerverről, ha rákattintasz a szemre — és az a lekérés bekerül az
 * audit logba. Elrejtéskor a memóriából is töröljük.
 */
function SecretRow({ label, preview, field, configured }) {
  const { toast } = useToast();
  const [revealed, setRevealed] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (revealed) { setRevealed(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/owner/catalog/whop-secrets');
      if (!data[field]) { toast.error(`No ${label.toLowerCase()} is configured on the server.`); return; }
      setRevealed(data[field]);
    } catch {
      toast.error('Could not load the secret.');
    } finally {
      setLoading(false);
    }
  };

  if (!configured) {
    return (
      <div className="surface-sunken px-3.5 py-2.5 flex items-center justify-between gap-3">
        <span className="text-xs text-gray-500">{label}</span>
        <span className="badge-banned">Missing</span>
      </div>
    );
  }

  return (
    <div className="surface-sunken px-3.5 py-2.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-600">{label}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={toggle}
            disabled={loading}
            title={revealed ? 'Hide' : 'Reveal'}
            aria-label={revealed ? 'Hide secret' : 'Reveal secret'}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md
                       text-gray-500 hover:text-gray-200 hover:bg-white/[0.07]
                       transition-colors disabled:opacity-40"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                 strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
              {revealed ? (
                <>
                  <path d="M3 3l18 18" />
                  <path d="M10.6 10.6a2 2 0 002.8 2.8" />
                  <path d="M9.4 5.2A9.5 9.5 0 0112 5c5 0 9 4.5 9 7a11 11 0 01-2.3 3.4M6.5 6.6C4.2 8.1 3 10.3 3 12c0 2.5 4 7 9 7a9.6 9.6 0 004-.9" />
                </>
              ) : (
                <>
                  <path d="M3 12s3.6-7 9-7 9 7 9 7-3.6 7-9 7-9-7-9-7z" />
                  <circle cx="12" cy="12" r="2.5" />
                </>
              )}
            </svg>
          </button>
          {revealed && <CopyButton value={revealed} variant="icon" />}
        </div>
      </div>
      <code className="block font-mono text-xs text-gray-300 break-all select-all">
        {loading ? '…' : (revealed || preview)}
      </code>
    </div>
  );
}
const EMPTY_PLAN = { label: '', days: 30, price: '', whop_plan_id: '', is_active: 1 };

/** Termék űrlap — új felvételhez és szerkesztéshez is. */
function ProductModal({ product, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState(product ?? EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const isEdit = !!product?.id;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = isEdit
        ? await api.put(`/owner/catalog/products/${product.id}`, form)
        : await api.post('/owner/catalog/products', form);
      toast.success(data.message);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the product.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      size="lg"
      title={isEdit ? 'Edit product' : 'New product'}
      subtitle="This is what customers see on the landing page and the product page."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Name</label>
          <input className="input" value={form.name} maxLength={255} required
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <div>
          <label className="label">Description</label>
          <textarea className="textarea min-h-[7rem]" value={form.description} maxLength={5000} required
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
        <div>
          <label className="label">Image URL</label>
          <input className="input font-mono text-sm" value={form.image_url || ''} maxLength={255}
            placeholder="/images/product.png"
            onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} />
          <p className="text-xs text-gray-600 mt-2">
            A public path (e.g. <span className="font-mono">/images/product.png</span>) or a full https URL.
          </p>
        </div>

        {isEdit && (
          <label className="flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer
                            bg-white/[0.02] border-white/[0.07] hover:border-white/[0.14]
                            transition-colors">
            <input type="checkbox" className="checkbox mt-0.5" checked={!!form.is_active}
              onChange={e => setForm(f => ({ ...f, is_active: e.target.checked ? 1 : 0 }))} />
            <span>
              <span className="block text-sm text-gray-200">Visible in the shop</span>
              <span className="block text-xs text-gray-600 mt-0.5">
                Unchecking hides the product from customers but keeps every order and key intact.
              </span>
            </span>
          </label>
        )}

        <div className="flex gap-3 pt-1">
          <button type="submit" className="btn-primary flex-1 py-2.5" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create product'}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

/** Csomag űrlap — ár és a hozzá tartozó Whop plan. */
function PlanModal({ productId, plan, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState(plan ?? EMPTY_PLAN);
  const [saving, setSaving] = useState(false);
  const isEdit = !!plan?.id;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        label: form.label,
        days: parseInt(form.days, 10),
        price: parseFloat(form.price),
        whop_plan_id: form.whop_plan_id?.trim() || '',
        is_active: form.is_active ? 1 : 0,
      };
      const { data } = isEdit
        ? await api.put(`/owner/catalog/plans/${plan.id}`, payload)
        : await api.post(`/owner/catalog/products/${productId}/plans`, payload);
      toast.success(data.message);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the plan.');
    } finally {
      setSaving(false);
    }
  };

  const days = parseInt(form.days, 10);
  const price = parseFloat(form.price);
  const perDay = Number.isFinite(days) && Number.isFinite(price) && days > 0 && days < 36000
    ? (price / days).toFixed(2)
    : null;

  return (
    <Modal
      size="lg"
      title={isEdit ? 'Edit plan' : 'New plan'}
      subtitle="Duration and price. 36000 days or more is shown to customers as Lifetime."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Label</label>
          <input className="input" value={form.label} maxLength={50} required placeholder="30 Days"
            onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Days</label>
            <input type="number" min="1" max="100000" className="input" value={form.days} required
              onChange={e => setForm(f => ({ ...f, days: e.target.value }))} />
          </div>
          <div>
            <label className="label">Price (€)</label>
            <input type="number" min="0" step="0.01" className="input" value={form.price} required
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
          </div>
        </div>

        {perDay && (
          <p className="text-xs text-gray-600 -mt-1">
            Customers will see <span className="text-gray-400">€{perDay}/day</span>.
          </p>
        )}

        <div>
          <label className="label">Whop plan ID</label>
          <input className="input font-mono text-sm" value={form.whop_plan_id || ''} maxLength={64}
            placeholder="plan_XXXXXXXXXX"
            onChange={e => setForm(f => ({ ...f, whop_plan_id: e.target.value }))} />
          <p className="text-xs text-gray-600 mt-2 leading-relaxed">
            The Whop plan that card payments charge for THIS plan. The amount comes from
            Whop's side, so it must match the price above — otherwise the customer is
            charged a different amount than the shop shows.
          </p>
        </div>

        {isEdit && (
          <label className="flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer
                            bg-white/[0.02] border-white/[0.07] hover:border-white/[0.14]
                            transition-colors">
            <input type="checkbox" className="checkbox mt-0.5" checked={!!form.is_active}
              onChange={e => setForm(f => ({ ...f, is_active: e.target.checked ? 1 : 0 }))} />
            <span>
              <span className="block text-sm text-gray-200">Sold in the shop</span>
              <span className="block text-xs text-gray-600 mt-0.5 leading-relaxed">
                Switch off to retire a plan you no longer sell. It disappears from the shop and
                cannot be ordered, but its past orders and keys stay untouched — which is why
                retiring beats deleting.
              </span>
            </span>
          </label>
        )}

        <div className="flex gap-3 pt-1">
          <button type="submit" className="btn-primary flex-1 py-2.5" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create plan'}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

export default function ProductsTab() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productModal, setProductModal] = useState(null);   // { product } | { product: null }
  const [planModal, setPlanModal] = useState(null);         // { productId, plan }
  const [confirmDelete, setConfirmDelete] = useState(null); // { kind, id, label }
  const [feeInput, setFeeInput] = useState('');
  const [feeSaving, setFeeSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/owner/catalog');
      setData(res.data);
      setFeeInput(String(Math.round((res.data.cardFeePercent ?? 0) * 100)));
    } catch {
      toast.error('Could not load the catalog.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    try {
      const url = target.kind === 'product'
        ? `/owner/catalog/products/${target.id}`
        : `/owner/catalog/plans/${target.id}`;
      const { data: res } = await api.delete(url);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete.');
    }
  };

  const saveFee = async (e) => {
    e.preventDefault();
    setFeeSaving(true);
    try {
      const { data: res } = await api.post('/owner/catalog/card-fee', { percent: parseFloat(feeInput) });
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the fee.');
    } finally {
      setFeeSaving(false);
    }
  };

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner label="Loading catalog…" /></div>;
  if (!data) return null;

  // Csak az élő csomagoknál számít a hiányzó Whop plan — a kivezetetteket
  // úgysem lehet megvenni.
  const missingWhop = data.products
    .flatMap(p => p.plans)
    .filter(pl => pl.is_active && !pl.whop_plan_id);

  return (
    <div className="space-y-6">
      {productModal && (
        <ProductModal product={productModal.product} onClose={() => setProductModal(null)} onSaved={load} />
      )}
      {planModal && (
        <PlanModal productId={planModal.productId} plan={planModal.plan}
          onClose={() => setPlanModal(null)} onSaved={load} />
      )}
      {confirmDelete && (
        <ConfirmModal
          title={`Delete ${confirmDelete.kind}?`}
          message={`"${confirmDelete.label}" — this cannot be undone.`}
          confirmLabel="Delete" danger
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Fizetési beállítások */}
      <div className="grid md:grid-cols-2 gap-6 items-start">
        <PanelCard
          title="Card fee"
          subtitle="Added on top of the base price for card payments. The product page shows this exact number, so the label can no longer disagree with the amount charged."
        >
          <form onSubmit={saveFee} className="flex items-end gap-3">
            <div className="flex-1">
              <label className="label">Fee (%)</label>
              <input type="number" min="0" max="100" step="1" className="input"
                value={feeInput} onChange={e => setFeeInput(e.target.value)} />
            </div>
            <button type="submit" className="btn-primary py-2.5" disabled={feeSaving}>
              {feeSaving ? 'Saving…' : 'Save'}
            </button>
          </form>
        </PanelCard>

        <PanelCard
          title="Whop connection"
          subtitle="Read-only. These live in the server's .env file — this panel shows them, it does not change them."
        >
          <div className="space-y-3">
            <SecretRow label="API key"
              preview={data.whopApiKeyPreview} field="apiKey" configured={data.whopApiKeyConfigured} />
            <SecretRow label="Webhook secret"
              preview={data.whopWebhookSecretPreview} field="webhookSecret"
              configured={!!data.whopWebhookSecretPreview} />

            <div className="surface-sunken px-3.5 py-2.5 flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500 flex-shrink-0">Fallback plan</span>
              <span className="font-mono text-xs text-gray-400 truncate">
                {data.whopFallbackPlanId || '—'}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-3 leading-relaxed">
            The fallback plan is only used by plans that have no Whop plan ID of their own.
          </p>
        </PanelCard>
      </div>

      {missingWhop.length > 0 && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg text-sm
                        bg-amber-500/10 border border-amber-500/25 text-amber-300">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
               strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 mt-0.5">
            <path d="M12 9v4M12 17h.01" />
            <path d="M10.3 3.86l-8.18 14a1.5 1.5 0 001.3 2.14h16.16a1.5 1.5 0 001.3-2.14l-8.18-14a1.5 1.5 0 00-2.6 0z" />
          </svg>
          <span className="leading-relaxed">
            <strong className="text-amber-200">
              {missingWhop.length} plan{missingWhop.length === 1 ? '' : 's'} without a Whop plan ID
            </strong>{' '}
            ({missingWhop.map(p => p.label).join(', ')}). Card payments for these fall back to the
            single global plan, which charges its own price regardless of what the shop shows.
          </span>
        </div>
      )}

      {/* Termékek */}
      <PanelCard
        title="Products"
        subtitle={`${data.products.length} product(s) in the catalog.`}
        actions={
          <button onClick={() => setProductModal({ product: null })} className="btn-primary text-sm py-2">
            New product
          </button>
        }
      >
        {data.products.length === 0 ? (
          <EmptyState
            icon={<><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18M16 10a4 4 0 01-8 0" /></>}
            title="No products yet."
            hint="Create one to make the shop sell something."
          />
        ) : (
          <div className="space-y-4">
            {data.products.map(product => (
              <div key={product.id} className="surface-sunken overflow-hidden">
                <div className="flex items-start justify-between gap-3 px-4 py-3.5 border-b border-white/[0.05]">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-semibold text-white">{product.name}</h3>
                      {product.is_active
                        ? <span className="badge-active">Visible</span>
                        : <span className="badge-inactive">Hidden</span>}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-1">{product.description}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => setProductModal({ product })} title="Edit product"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-md
                                 text-gray-500 hover:text-white hover:bg-white/[0.07] transition-colors">
                      <EditIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete({ kind: 'product', id: product.id, label: product.name })}
                      title="Delete product"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-md
                                 text-gray-500 hover:text-red-300 hover:bg-red-500/10 transition-colors">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Mobilon kártyák: a hat oszlopos tábla 375 px-en ~250 px oldalra
                    görgetést kért, és a szerkesztés/törlés gomb a görgetés végén volt. */}
                <div className="md:hidden divide-y divide-white/[0.05]">
                  {product.plans.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-gray-600">
                      No plans yet — this product cannot be bought.
                    </p>
                  ) : product.plans.map(plan => (
                    <div key={plan.id} className={`px-4 py-3 ${plan.is_active ? '' : 'opacity-55'}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-200 font-medium">{plan.label}</span>
                        {!plan.is_active && <span className="badge-inactive">retired</span>}
                        <span className="ml-auto text-white font-medium tabular-nums">€{plan.price}</span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500">
                        <span>{plan.days >= 36000 ? 'Lifetime' : `${plan.days} days`}</span>
                        <span className="tabular-nums">keys / orders: {plan.key_count} / {plan.order_count}</span>
                        {plan.whop_plan_id
                          ? <span className="font-mono text-gray-400 break-all">{plan.whop_plan_id}</span>
                          : <span className="badge bg-amber-500/12 text-amber-300 border-amber-500/25">Whop not set</span>}
                      </div>
                      <div className="mt-2.5 flex gap-2">
                        <button onClick={() => setPlanModal({ productId: product.id, plan })}
                          className="btn-secondary text-xs px-3 py-1.5">
                          <EditIcon className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button onClick={() => setConfirmDelete({ kind: 'plan', id: plan.id, label: plan.label })}
                          className="btn-secondary text-xs px-3 py-1.5 hover:text-red-300">
                          <TrashIcon className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="scroll-x hidden md:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/[0.05]">
                        <th className="text-left px-4 py-2.5 table-head">Plan</th>
                        <th className="text-left px-4 py-2.5 table-head">Days</th>
                        <th className="text-left px-4 py-2.5 table-head">Price</th>
                        <th className="text-left px-4 py-2.5 table-head">Whop plan</th>
                        <th className="text-left px-4 py-2.5 table-head">Keys / orders</th>
                        <th className="px-4 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.plans.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-600">
                            No plans yet — this product cannot be bought.
                          </td>
                        </tr>
                      ) : product.plans.map(plan => (
                        <tr key={plan.id} className={`border-b border-white/[0.05] last:border-0
                                                      hover:bg-white/[0.02] transition-colors ${
                          plan.is_active ? '' : 'opacity-55'
                        }`}>
                          <td className="px-4 py-2.5 text-gray-200">
                            <span className="flex items-center gap-2">
                              {plan.label}
                              {!plan.is_active && <span className="badge-inactive">retired</span>}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-gray-400 tabular-nums">
                            {plan.days >= 36000 ? 'Lifetime' : plan.days}
                          </td>
                          <td className="px-4 py-2.5 text-white font-medium tabular-nums">€{plan.price}</td>
                          <td className="px-4 py-2.5">
                            {plan.whop_plan_id
                              ? <span className="font-mono text-xs text-gray-400">{plan.whop_plan_id}</span>
                              : <span className="badge bg-amber-500/12 text-amber-300 border-amber-500/25">
                                  not set
                                </span>}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600 tabular-nums">
                            {plan.key_count} / {plan.order_count}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => setPlanModal({ productId: product.id, plan })}
                                title="Edit plan"
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md
                                           text-gray-500 hover:text-white hover:bg-white/[0.07] transition-colors">
                                <EditIcon className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setConfirmDelete({ kind: 'plan', id: plan.id, label: plan.label })}
                                title="Delete plan"
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md
                                           text-gray-500 hover:text-red-300 hover:bg-red-500/10 transition-colors">
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="px-4 py-3 border-t border-white/[0.05]">
                  <button
                    onClick={() => setPlanModal({ productId: product.id, plan: null })}
                    className="btn-secondary text-sm py-1.5"
                  >
                    Add plan
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </PanelCard>
    </div>
  );
}
